import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { EmployeeListQuery } from '@/api/handlers/resources/salaries/requests';
import type { GetEmployeesResponse } from '@/api/handlers/resources/salaries/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { SalaryQueryKeys } from './salaryQueryKeys';

const STALE_TIME_MS = 60_000;

/**
 * The employee register for a status filter + search. `keepPreviousData` keeps
 * the current rows on screen while a filter change loads, so the summary cards
 * and the comparison bar don't blink between two valid states.
 *
 * `enabled` exists because this endpoint is **OrgAdmin-only**: for anyone else
 * it can only 403, and a screen that fetches in order to discover that tells the
 * user nothing they can act on. The page derives the answer from the session and
 * passes it here — see `salariesAccess`.
 */
export const useEmployeesQuery = (query: EmployeeListQuery, enabled = true) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<GetEmployeesResponse>>({
        queryKey: SalaryQueryKeys.employees(query.status, query.search),
        queryFn: () => apiClient.salaries.getEmployees(query),
        staleTime: STALE_TIME_MS,
        placeholderData: keepPreviousData,
        enabled,
    });
};
